/* eslint-disable */
import { CONFIG } from "~/config";
import { frpc } from "~/util/general";
import { IReviewItem } from "~/review/interfaces";

const URL = "/mediaagg";
const MAX_OLD_DATA = 5*60*1000;

type SORT_TYPE = "default" | "createDate" | "takeDate" | "likeCount" | "viewCount" | "beauty";

interface IList {
	source: string;
	id: number | string;
	sortType?: SORT_TYPE,
	page?: number;
	beatyScore?: boolean;
}

interface IDetail {
	source: string;
	id: string | number;
	full?: boolean;
}

interface ISort {
	source: string;
	id: string | number;
	sortType?: SORT_TYPE;
	page?: number;
}

export type IProvider = {
	list: (data: IList) => Promise<IData>;
	detail: (data: IDetail) => Promise<IData>;
	sort: (data: ISort) => Promise<IData>;
	mediaDel: (id: string | number, isFirmPhoto: boolean) => Promise<any>;
	clear: (source: string, id: string | number) => void;
	getAuthor: (id: string | number) => Promise<IAuthor>;
	getStats: (id: string | number) => Promise<IAuthorStats>;
}

export const ITEM_ON_PAGE = 100;

type URL_TYPE = "placeholder" | "full";

interface IMark {
	lat: number;
	lon: number;
}

type USER_SOURCE = "rus" | "windy" | "internal";

interface IAuthorStats {
	mediaCount?: number;
	viewCount?: number;
	likeSum?: number;
}

interface IAuthor extends IAuthorStats {
	userId: number;
	name: string;
	userSource: USER_SOURCE;
	galleryBbox?: {
		bottomLeft: {
			lat: number;
			lon: number;
		};
		topRight: {
			lat: number;
			lon: number;
		};
	};
	userPublicId: string;
	avatarUrl: string;
	bankId: boolean;
}

interface IEXIF {
	key: string;
	name: string;
	value: string;
}

interface ILIKE {
	id: string;
	count: number;
	voted: number;
}

interface IPhotoAuthor {
	name: string;
	userSource: string;
	userId: number;
	userPublicId: string;
	mediaCount: number;
	bankId: boolean;
	avatarUrl: string;
	reviewCount: number;
	hasPremium: boolean;
	isFanda: boolean;
}

export interface IMedia {
	source: string;
	id: number;
	name: string;
	description: string;
	urls: {
		url: string;
		urlType: URL_TYPE;
	}
	mark: IMark;
	width: number;
	height: number;
	date: number;
	viewCount: number;
	likes: ILIKE;
	approved: boolean;
	exif: IEXIF;
	userPublicId:string;
	total: number;
	language: null | string;
	author: IPhotoAuthor;
	entitySource?: string;
	entityId?: number;
	entityTitle?: string;
	entityLocation?: any;
	entityPoiTypeId?: number;
	review?: IReviewItem;
	reviewId?: number;
	wikimedia?: {
		author: string;
		license: string;
		license_url: string;
	};
	beautyScore?: number;
	status: "approved" | "rejected";
}

export interface IRibbonMedia extends Omit<IMedia, "urls"> {
	urls: Array<{
		url: string;
		urlType: "full" | "placeholder";
	}>;
}

interface IData {
	error: string;
	status?: number;
	gallery?: Array<IMedia>;
	author?: IAuthor;
	total?: number;
}

interface IGalleryRibbon {
	error: string;
	status?: number;
	data?: {
		data: {
			gallery: Array<IRibbonMedia>;
		};
	};
	total?: number;
}

const CACHE_DATA: {
	[source: string]: {
		[id: string]: {
			time: number;
			full: boolean;
			data: Promise<IData>;
		};
	}
} | {
	[key in SORT_TYPE]: {
		[source: string]: {
			[id: string | number]: {
				[page: number]: {
					time: number;
					data: Promise<IData>;
				};
			};
		};
	}
} = {};

function jsonRequest(url: string, data: { query: string; }) {
	return new Promise((resolve, reject) => {
		const output = {
			data: null,
			status: 500,
			error: "",
		};
		const addHeaders = {};

		function handleError(error: any, status = 500) {
			output.error = error;
			output.status = status;
			reject(output);
		}

		// klikatko
		if (Mapy.CUS.userweb?.gqlGetFirmMediaFromMapy) {
			addHeaders["x-ab-feature"] = "getFirmMediaFromMapy";
		}

		fetch(url, {
			headers: {
				"Content-type": "application/json",
				...addHeaders,
			},
			method: "POST",
			body: JSON.stringify(data),
			credentials: "include",
		}).then(response => {
			response.json().then(jsonData => {
				output.status = 200;
				output.data = jsonData;
				resolve(output);
			}, error => handleError(error, response.status));
		}, error => handleError(error));
	});
}

export function list({
	source,
	id,
	sortType = "default",
	page = 0,
	beatyScore = false,
}: IList): Promise<IData> {
	const itemOnPage = Mapy.CUS.gallery && Mapy.CUS.gallery.limit ? Mapy.CUS.gallery.limit : ITEM_ON_PAGE;

	//bez sortu:
	//gallery: ${source === "author" ? `getAuthorGallery(authorId: ${id})` : `getGallery(entitySource: "${source}", entityId: ${id})`} {
	//
	//se sortem:
	//gallery: ${source === "author" ? `getAuthorGallery(authorId: ${id}, sort: ${sortType})` : `getGallery(entitySource: "${source}", entityId: ${id}${sortType != "default" ? `, sort: ${sortType}` : ``})`} {
	const query = {
		query: `{
		gallery: ${source === "author"
		? `getAuthorGallery(userPublicId: "${id}", sort: ${sortType}, limit: ${itemOnPage}, offset: ${page*itemOnPage})`
		: `getGallery(entitySource: "${source}", entityId: ${id}, limit: ${itemOnPage}, offset: ${page*itemOnPage}${sortType === "default" ? "" : `, sort: ${sortType}`}, wikimedia: true)`} {
			id
			source
			name
			width
			height
			userPublicId
			mark {
				lat
				lon
			}
			urls {
				url
				urlType
			}
			date
			likes {
				id
				count
				voted
			}
			viewCount
			language
			${source === "author" ? `approved` : ``}
			${source !== "author" ? `reviewId` : ``}
			${["author"].indexOf(source) === -1 ? `total` : ``}
			wikimedia
			${beatyScore ? `beautyScore` : ``}
			status
		}
		${source === "author"
		? `author: getAuthor(userPublicId: "${id}") {
			name
			userSource
			userId
			userPublicId
			mediaCount
			viewCount
			galleryBbox
			likeSum
			bankId
			avatarUrl
			hasPremium
			isFanda
		}`
		: ``}
	}`,
	};

	if (!CACHE_DATA[sortType] || !CACHE_DATA[sortType][source] || !CACHE_DATA[sortType][source][id] || !CACHE_DATA[sortType][source][id][page] || Date.now() - CACHE_DATA[sortType][source][id][page].time > MAX_OLD_DATA) {
		if (!CACHE_DATA[sortType]) {
			CACHE_DATA[sortType] = {};
		}

		if (!CACHE_DATA[sortType][source]) {
			CACHE_DATA[sortType][source] = {};
		}

		if (!CACHE_DATA[sortType][source][id]) {
			CACHE_DATA[sortType][source][id] = {};
		}

		const request = jsonRequest(`${CONFIG.requestUrl}${URL}/gql`, query);

		CACHE_DATA[sortType][source][id][page] = {
			time: Date.now(),
			data: request.then((res: any) => ({
				error: res.error,
				status: res.status,
				gallery: res.data && res.data.data && res.data.data.gallery ? res.data.data.gallery : [],
				author: res.data && res.data.data && res.data.data.author ? res.data.data.author : null,
				total: source === "author"
					? res.data && res.data.data && res.data.data.author && "mediaCount" in res.data.data.author
						? res.data.data.author.mediaCount
						: 0
					: res.data && res.data.data && res.data.data.gallery && res.data.data.gallery.length
						? res.data.data.gallery[0].total
						: undefined,
			}), () => {
				delete CACHE_DATA[sortType][source][id][page];

				return {
					error: true,
				};
			}) as Promise<IData>,
		};
	}

	return CACHE_DATA[sortType][source][id][page].data;
}

export function detail({
	source,
	id,
	full = false,
}: IDetail): Promise<IData> {
	let fields = `
				description
				author {
					name
					userSource
					userId
					userPublicId
					mediaCount
					bankId
					avatarUrl
					hasPremium
					isFanda
				}
				exif {
					key
					name
					value
				}
				language
				entitySource
				entityId
				entityTitle
				entityLocation
				entityPoiTypeId
				review
				wikimedia
				status
	`;

	if (full) {
		fields += `
				id
				width
				height
				source
				name
				mark {
					lat
					lon
				}
				urls {
					url
					urlType
				}
				date
				likes {
					id
					count
					voted
				}
				viewCount
				approved
		`;
	}

	const query = {
		query: `{
			detail: getPhoto(photoSource: "${source}", photoId: ${id}, lang: "${CONFIG.defaultLang}", wikimedia: true, forceTranslation: true) {
				${fields}
			}
		}`,
	};

	if (!CACHE_DATA[source] || !CACHE_DATA[source][id] || Date.now() - CACHE_DATA[source][id].time > MAX_OLD_DATA || (CACHE_DATA[source] && CACHE_DATA[source][id] && !CACHE_DATA[source][id].full && full)) {
		if (!CACHE_DATA[source]) {
			CACHE_DATA[source] = {};
		}

		const request = jsonRequest(`${CONFIG.requestUrl}${URL}/gql`, query);

		CACHE_DATA[source][id] = {
			time: Date.now(),
			full: !!full,
			data: request.then((res: any) => ({
				error: res.error,
				status: res.status,
				gallery: res.data && res.data.data && res.data.data.detail ? [res.data.data.detail] : [],
			}), () => {
				delete CACHE_DATA[source][id];

				return {
					error: true,
				};
			}) as Promise<IData>,
		};
	}

	return CACHE_DATA[source][id].data;
}

export function sort({
	source,
	id,
	sortType = "default",
	page = 0,
}: ISort): Promise<IData> {
	const query = {
		query: `{
			gallery: ${source === "author" ? `getAuthorGallery(userPublicId: "${id}", sort: ${sortType})` : `getGallery(entitySource: "${source}", entityId: ${id}${sortType === "default" ? `` : `, sort: ${sortType}`})`} {
				likes {
					id
				}
			}
		}`,
	};

	if (!CACHE_DATA[sortType] || !CACHE_DATA[sortType][source] || !CACHE_DATA[sortType][source][id] || !CACHE_DATA[sortType][source][id][page] || Date.now() - CACHE_DATA[sortType][source][id][page].time > MAX_OLD_DATA) {
		if (!CACHE_DATA[sortType]) {
			CACHE_DATA[sortType] = {};
		}

		if (!CACHE_DATA[sortType][source]) {
			CACHE_DATA[sortType][source] = {};
		}

		if (!CACHE_DATA[sortType][source][id]) {
			CACHE_DATA[sortType][source][id] = {};
		}

		const request = jsonRequest(`${CONFIG.requestUrl}${URL}/gql`, query);

		CACHE_DATA[sortType][source][id][page] = {
			time: Date.now(),
			data: request.then((res: any) => ({
				error: res.error,
				status: res.status,
				gallery: res.data && res.data.data && res.data.data.gallery ? res.data.data.gallery : [],
				author: res.data && res.data.data && res.data.data.author ? res.data.data.author : null,
			}), () => {
				delete CACHE_DATA[sortType][source][id];

				return {
					error: true,
				};
			}),
		};
	}

	return CACHE_DATA[sortType][source][id][page].data;
}

export function mediaDel(id: string | number, isFirmPhoto: boolean): Promise<any> {
	const args = [id];

	if (isFirmPhoto) {
		args.push("firm");
	}

	return frpc({
		url: `${CONFIG.requestUrl}/mediarpc/`,
		method: "media.del",
		args,
	}).promise;
}

export function clear(source: string, id: string | number) {
	if (CACHE_DATA[source] && CACHE_DATA[source][id]) {
		delete CACHE_DATA[source][id];
	}

	for (const sortType in CACHE_DATA) {
		if (CACHE_DATA[sortType][source] && CACHE_DATA[sortType][source][id]) {
			delete CACHE_DATA[sortType][source][id];
		}
	}
}

export function getAuthor(id: string | number): Promise<IAuthor> {
	let authorIdStr = "";

	if (id) {
		if (id.toString().match(/^\d+$/u)) {
			authorIdStr = `authorId: ${id}`;
		} else {
			authorIdStr = `userPublicId: "${id}"`;
		}
	}

	const query = {
		query: `{
			author: getAuthor${id ? `(${authorIdStr})` : ``} {
				name
				userSource
				userId
				userPublicId
				bankId
				avatarUrl
				hasPremium
				isFanda
			}
		}`,
	};

	return jsonRequest(`${CONFIG.requestUrl}${URL}/gql`, query).then((res: any) => res.data.data, () => {});
}

export function getStats(id: string | number): Promise<IAuthorStats> {
	let authorIdStr = "";

	if (id) {
		if (id.toString().match(/^\d+$/u)) {
			authorIdStr = `authorId: ${id}`;
		} else {
			authorIdStr = `userPublicId: "${id}"`;
		}
	}

	const query = {
		query: `{
			author: getAuthor${id ? `(${authorIdStr})` : ``} {
				mediaCount
				viewCount
				likeSum
			}
		}`,
	};

	return jsonRequest(`${CONFIG.requestUrl}${URL}/gql`, query).then((res: any) => res.data.data, () => {});
}

interface IGetGalleryRibbon {
	source: string;
	id: number;
	zoom: number;
	bbox: Array<Array<number>>;
	limit?: number;
}

export function getGalleryRibbon({
	source,
	id,
	zoom,
	bbox,
	limit = 5,
}: IGetGalleryRibbon): Promise<IGalleryRibbon> {
	const query = `{
		gallery: getGalleryRibbon(
		  entitySource: "${source}",
		  entityId: ${id},
		  bbox: [${bbox[0][0]}, ${bbox[0][1]}, ${bbox[1][0]}, ${bbox[1][1]}],
		  zoom: ${zoom},
		  limit: ${limit},
		  sort: beauty) {
		  id
		  source
		  name
		  width
		  height
		  userPublicId
		  mark {
			lat
			lon
		  }
		  urls {
			url
			urlType
		  }
		  date
		  likes {
			id
			count
			voted
		  }
		  viewCount
		  language
		  reviewId
		  total
		  wikimedia
		}}`;

	return jsonRequest(`${CONFIG.requestUrl}${URL}/gql`, { query }) as Promise<IGalleryRibbon>;
}
